/**
 * Stream Chat client singleton for the web app.
 *
 * Usage:
 *   import { connectStreamClient, disconnectStreamClient } from '@/lib/streamClient';
 *   // in a React effect after sign-in:
 *   await connectStreamClient(cognitoSub, tokenProvider);
 *   // on sign-out:
 *   await disconnectStreamClient();
 *
 * `tokenProvider` is a `() => Promise<string>` that invokes the
 * `getStreamChatToken` AppSync mutation and returns the minted JWT. The
 * Stream SDK calls it on connect and again automatically when the current
 * token is near expiry, so the client transparently refreshes tokens.
 */
import { StreamChat } from 'stream-chat';

const apiKey = (import.meta.env.VITE_STREAM_CHAT_API_KEY as string | undefined) ?? '';

export const streamClient = StreamChat.getInstance(apiKey || 'stream-key-missing');

let currentUserId: string | null = null;
let connectingPromise: Promise<void> | null = null;

/**
 * Connects the Stream client as `cognitoSub` using `tokenProvider` to mint
 * short-lived JWTs. Subsequent calls for the same user are no-ops; calls for
 * a different user disconnect first.
 */
export async function connectStreamClient(
  cognitoSub: string,
  tokenProvider: () => Promise<string>,
): Promise<void> {
  if (!apiKey) {
    throw new Error(
      'VITE_STREAM_CHAT_API_KEY is not configured — set it in frontend/.env',
    );
  }
  if (currentUserId === cognitoSub && streamClient.userID === cognitoSub) {
    return;
  }
  if (connectingPromise) return connectingPromise;

  connectingPromise = (async () => {
    if (streamClient.userID && streamClient.userID !== cognitoSub) {
      await streamClient.disconnectUser();
    }
    await streamClient.connectUser({ id: cognitoSub }, tokenProvider);
    currentUserId = cognitoSub;
  })();
  try {
    await connectingPromise;
  } finally {
    connectingPromise = null;
  }
}

export async function disconnectStreamClient(): Promise<void> {
  if (streamClient.userID) {
    await streamClient.disconnectUser();
  }
  currentUserId = null;
}
